import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import type { SendTemplatedEmailPayload } from "@/lib/data/requests/contacts/contact-templated-email";
import type { SendMatterEmailPayload } from "@/lib/data/requests/matters/matter-email";
import type {
  MATTER_CONTACT_CONNECTION_LABEL_OPTIONS,
  MATTER_TEMPLATE_CATEGORY_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  APIResponse,
  Contact,
  Matter,
  MatterContactConnection,
  MatterExpense,
  MatterFile,
  MatterIncidentReport,
  MatterInsurance,
  MatterInsuranceData,
  MatterLien,
  MatterLitigationDate,
  MatterLostWage,
  MatterDocumentSubItem,
  MatterNote,
  MatterPropertyDamage,
  MatterSettlementCounter,
  MatterSettlementDemand,
  MatterSettlementOffer,
  MatterSubItemType,
  MatterTask,
  MatterTrustCheck,
  MatterVehicle,
  MedicalTreatment,
  MedicalTreatmentAppointment,
  MedicalTreatmentBalanceConfirmation,
  MedicalTreatmentBill,
  MedicalTreatmentPlan,
  MedicalTreatmentRecord,
} from "@/lib/types";
import type { ReportsSignatureRequestReport } from "@/lib/types/Reports";
import type { MatterContactLabelPair } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useModals } from "@/providers/ModalProvider";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import type { z } from "zod";

export const useGlobalModal = () => {
  const { openSlideover, closeSlideover, closeAllSlideovers, getSlideover } =
    useModals();

  const openCommunicationModal = (payload: {
    contactId: string;
    matterId?: string;
    setSelectedContactId?: (contactId: string) => void;
    defaultMessage?: string;
  }) => {
    return openSlideover({
      type: "communication",
      title: "Communication",
      description: "Send a text message to a contact",
      ...payload,
    });
  };

  const openPhoneCallLogModal = (payload: { contactId: string }) => {
    return openSlideover({
      type: "phone-call-log",
      title: "Phone calls",
      description: "Review and record phone calls for this contact.",
      ...payload,
    });
  };

  const openMatterEmailViewModal = (payload: {
    matterId: string;
    matterEmailId: string;
  }) => {
    const existingModal = getSlideover("matter-email-view");
    if (existingModal) {
      closeSlideover(existingModal.id);
    }

    return openSlideover({
      type: "matter-email-view",
      title: "View email",
      description: "Review the email content, recipients, and attachments.",
      size: "large",
      ...payload,
    });
  };

  const openSendEmailModal = (payload: {
    toEmails: string[];
    contactIds: string[];
    templateName: string;
    templateData: SendTemplatedEmailPayload["templateData"];
    title?: string;
    description?: string;
    defaultCcEmails?: string[];
    defaultBccEmails?: string[];
    onSuccess?: () => void;
  }) => {
    const {
      title = "Send email",
      description = "Review and send your email.",
      ...rest
    } = payload;

    return openSlideover({
      type: "send-email",
      title,
      description,
      ...rest,
    });
  };

  const openSendMatterEmailModal = (payload: {
    matterId: string;
    contactId?: string;
    toEmails?: string[];
    templateName?: SendMatterEmailPayload["templateName"];
    templateData?: Partial<SendMatterEmailPayload["templateData"]>;
    matterFiles?: string[];
    title?: string;
    description?: string;
    onSuccess?: () => void;
  }) => {
    const {
      title = "Send email",
      description = "Compose and send your email.",
      ...rest
    } = payload;

    return openSlideover({
      type: "send-matter-email",
      title,
      description,
      ...rest,
    });
  };

  const openVehicleModal = (payload: {
    matterId: string;
    vehicleId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    setSelectedVehicleId?: (vehicleId: string) => void;
    enforceRequiredContact?: "Client" | "Defendant";
    requiredVehiclePartyContactId?: string;
    initialData?: Partial<MatterVehicle>;
    defendantContactIdContext?: string;
    vehiclePartyRole?: "Client" | "Defendant" | "OtherParty";
    sourceMatterContactConnectionId?: string;
  }) => {
    return openSlideover({
      type: "vehicle",
      title: payload.editing
        ? SLIDEOVER_TITLE.VEHICLE.UPDATE
        : SLIDEOVER_TITLE.VEHICLE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.VEHICLE.UPDATE
        : SLIDEOVER_DESCRIPTION.VEHICLE.CREATE,
      ...payload,
    });
  };

  const openInsuranceModal = (payload: {
    matterId: string;
    insuranceId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    afterCreateFn?: (newInsurance: APIResponse<MatterInsuranceData>) => void;
    enforceRequiredContact?: "Client" | "Defendant";
    requiredInsurancePartyContactId?: string;
    initialData?: Partial<MatterInsurance>;
    defendantContactIdContext?: string;
    insurancePartyRole?: "Client" | "Defendant" | "OtherParty";
    sourceMatterContactConnectionId?: string;
  }) => {
    return openSlideover({
      type: "insurance",
      title: payload.editing
        ? SLIDEOVER_TITLE.INSURANCE.UPDATE
        : SLIDEOVER_TITLE.INSURANCE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.INSURANCE.UPDATE
        : SLIDEOVER_DESCRIPTION.INSURANCE.CREATE,
      ...payload,
    });
  };

  const openPropertyDamageModal = (payload: {
    matterId: string;
    propertyDamageId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterPropertyDamage>;
  }) => {
    return openSlideover({
      type: "property-damage",
      title: payload.editing
        ? SLIDEOVER_TITLE.PROPERTY_DAMAGE.UPDATE
        : SLIDEOVER_TITLE.PROPERTY_DAMAGE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.PROPERTY_DAMAGE.UPDATE
        : SLIDEOVER_DESCRIPTION.PROPERTY_DAMAGE.CREATE,
      ...payload,
    });
  };

  const openIncidentReportModal = (payload: {
    matterId: string;
    incidentReportId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterIncidentReport>;
  }) => {
    return openSlideover({
      type: "incident-report",
      title: payload.editing
        ? SLIDEOVER_TITLE.INCIDENT_REPORT.UPDATE
        : SLIDEOVER_TITLE.INCIDENT_REPORT.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.INCIDENT_REPORT.UPDATE
        : SLIDEOVER_DESCRIPTION.INCIDENT_REPORT.CREATE,
      ...payload,
    });
  };

  const openTaskModal = (payload: {
    matterId: string;
    massTortId?: string;
    taskId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterTask>;
    hideMatterField?: boolean;
    subItem?: {
      id: string;
      type: MatterSubItemType;
    };
    slideoverId?: string;
  }) => {
    return openSlideover({
      type: "matter-task",
      title: payload.editing
        ? SLIDEOVER_TITLE.MATTER_TASK.UPDATE
        : SLIDEOVER_TITLE.MATTER_TASK.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.MATTER_TASK.UPDATE
        : SLIDEOVER_DESCRIPTION.MATTER_TASK.CREATE,
      matterId: payload.matterId,
      massTortId: payload.massTortId,
      taskId: payload.taskId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      subItem: payload.subItem,
      initialData: payload.initialData,
      hideMatterField: payload.hideMatterField,
      slideoverId: payload.slideoverId,
    });
  };

  const openLienModal = (payload: {
    matterId: string;
    lienId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterLien>;
  }) => {
    return openSlideover({
      type: "lien",
      title: payload.editing
        ? SLIDEOVER_TITLE.LIEN.UPDATE
        : SLIDEOVER_TITLE.LIEN.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.LIEN.UPDATE
        : SLIDEOVER_DESCRIPTION.LIEN.CREATE,
      matterId: payload.matterId,
      lienId: payload.lienId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
    });
  };

  const openLostWageModal = (payload: {
    matterId: string;
    lostWageId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterLostWage>;
  }) => {
    return openSlideover({
      type: "lost-wage",
      title: payload.editing
        ? SLIDEOVER_TITLE.LOST_WAGE.UPDATE
        : SLIDEOVER_TITLE.LOST_WAGE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.LOST_WAGE.UPDATE
        : SLIDEOVER_DESCRIPTION.LOST_WAGE.CREATE,
      matterId: payload.matterId,
      lostWageId: payload.lostWageId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
    });
  };

  const openLitigationDateModal = (payload: {
    matterId: string;
    litigationDateId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterLitigationDate>;
  }) => {
    return openSlideover({
      type: "matter-litigation-date",
      title: payload.editing
        ? SLIDEOVER_TITLE.MATTER_LITIGATION_DATE.UPDATE
        : SLIDEOVER_TITLE.MATTER_LITIGATION_DATE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.MATTER_LITIGATION_DATE.UPDATE
        : SLIDEOVER_DESCRIPTION.MATTER_LITIGATION_DATE.CREATE,
      matterId: payload.matterId,
      litigationDateId: payload.litigationDateId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
    });
  };

  const openLitigationDatePlanConnectionModal = (payload: {
    matterId: string;
  }) => {
    return openSlideover({
      type: "matter-litigation-date-plan-connection",
      title: SLIDEOVER_TITLE.MATTER_LITIGATION_DATE_PLAN_CONNECTION.CREATE,
      description:
        SLIDEOVER_DESCRIPTION.MATTER_LITIGATION_DATE_PLAN_CONNECTION.CREATE,
      matterId: payload.matterId,
    });
  };

  const openNoteModal = (payload: {
    matterId: string;
    noteId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterNote>;
    hasEditPermission?: boolean;
    hasDeletePermission?: boolean;
    subItem?: {
      id: string;
      type: MatterSubItemType;
    };
  }) => {
    return openSlideover({
      type: "matter-note",
      title: payload.editing
        ? SLIDEOVER_TITLE.NOTE.UPDATE
        : SLIDEOVER_TITLE.NOTE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.NOTE.UPDATE
        : SLIDEOVER_DESCRIPTION.NOTE.CREATE,
      matterId: payload.matterId,
      noteId: payload.noteId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
      hasEditPermission: payload.hasEditPermission,
      hasDeletePermission: payload.hasDeletePermission,
      subItem: payload.subItem,
    });
  };

  const openSettlementDemandModal = (payload: {
    matterId: string;
    settlementId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    selectedInsurance?: MatterInsurance;
    negotiationType?: "Demand" | "Offer";
    isMedpay?: boolean;
    coverageType?: string;
    initialData?:
      | Partial<MatterSettlementDemand>
      | Partial<MatterSettlementOffer>;
  }) => {
    return openSlideover({
      type: "settlement-demand",
      title: payload.editing
        ? payload.negotiationType === "Demand"
          ? SLIDEOVER_TITLE.SETTLEMENT_DEMAND.UPDATE
          : SLIDEOVER_TITLE.SETTLEMENT_OFFER.UPDATE
        : payload.negotiationType === "Demand"
          ? SLIDEOVER_TITLE.SETTLEMENT_DEMAND.CREATE
          : SLIDEOVER_TITLE.SETTLEMENT_OFFER.CREATE,
      description: payload.editing
        ? payload.negotiationType === "Demand"
          ? SLIDEOVER_DESCRIPTION.SETTLEMENT_DEMAND.UPDATE
          : SLIDEOVER_DESCRIPTION.SETTLEMENT_OFFER.UPDATE
        : payload.negotiationType === "Demand"
          ? SLIDEOVER_DESCRIPTION.SETTLEMENT_DEMAND.CREATE
          : SLIDEOVER_DESCRIPTION.SETTLEMENT_OFFER.CREATE,
      matterId: payload.matterId,
      settlementId: payload.settlementId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      negotiationType: payload.negotiationType ?? "Demand",
      selectedInsurance: payload.selectedInsurance,
      isMedpay: payload.isMedpay,
      coverageType: payload.coverageType,
      initialData: payload.initialData,
    });
  };

  const openSettlementCounterModal = (payload: {
    matterId: string;
    settlementCounterId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    selectedInsurance?: MatterInsurance;
    coverageType?: string;
    initialData?: Partial<MatterSettlementCounter>;
  }) => {
    return openSlideover({
      type: "settlement-counter",
      title: payload.editing
        ? SLIDEOVER_TITLE.SETTLEMENT_COUNTER.UPDATE
        : SLIDEOVER_TITLE.SETTLEMENT_COUNTER.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.SETTLEMENT_COUNTER.UPDATE
        : SLIDEOVER_DESCRIPTION.SETTLEMENT_COUNTER.CREATE,
      matterId: payload.matterId,
      settlementCounterId: payload.settlementCounterId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      selectedInsurance: payload.selectedInsurance,
      coverageType: payload.coverageType,
      initialData: payload.initialData,
    });
  };

  const openExpenseModal = (payload: {
    matterId: string;
    expenseId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterExpense>;
    splitMode?: boolean;
  }) => {
    return openSlideover({
      type: "expense",
      title: payload.editing
        ? SLIDEOVER_TITLE.EXPENSE.UPDATE
        : payload.splitMode
          ? "Split costs"
          : SLIDEOVER_TITLE.EXPENSE.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.EXPENSE.UPDATE
        : payload.splitMode
          ? "Choose related matters to split a cost between"
          : SLIDEOVER_DESCRIPTION.EXPENSE.CREATE,
      matterId: payload.matterId,
      expenseId: payload.expenseId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
      splitMode: payload.splitMode,
    });
  };

  const openMedicalTreatmentModal = (payload: {
    matterId: string;
    treatmentId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MedicalTreatment>;
    initialProvider?: Contact;
  }) => {
    return openSlideover({
      type: "medical-treatment",
      title: payload.editing
        ? SLIDEOVER_TITLE.MEDICAL_TREATMENT.UPDATE
        : SLIDEOVER_TITLE.MEDICAL_TREATMENT.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.MEDICAL_TREATMENT.UPDATE
        : SLIDEOVER_DESCRIPTION.MEDICAL_TREATMENT.CREATE,
      matterId: payload.matterId,
      treatmentId: payload.treatmentId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
      initialProvider: payload.initialProvider,
    });
  };

  const openTreatmentAppointmentModal = (payload: {
    matterId: string;
    appointmentId?: string;
    treatmentId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MedicalTreatmentAppointment>;
  }) => {
    return openSlideover({
      type: "treatment-appointment",
      title: payload.editing
        ? SLIDEOVER_TITLE.TREATMENT_APPOINTMENT.UPDATE
        : SLIDEOVER_TITLE.TREATMENT_APPOINTMENT.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.TREATMENT_APPOINTMENT.UPDATE
        : SLIDEOVER_DESCRIPTION.TREATMENT_APPOINTMENT.CREATE,
      matterId: payload.matterId,
      appointmentId: payload.appointmentId,
      treatmentId: payload.treatmentId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
    });
  };

  const openAssociateTreatmentAppointmentsModal = (payload: {
    matterId: string;
    treatmentId: string;
    appointmentIds: string[];
    onSuccess?: () => void;
  }) => {
    return openSlideover({
      type: "associate-treatment-appointments",
      title: SLIDEOVER_TITLE.TREATMENT_APPOINTMENT_ASSOCIATION.CREATE,
      description:
        SLIDEOVER_DESCRIPTION.TREATMENT_APPOINTMENT_ASSOCIATION.CREATE,
      matterId: payload.matterId,
      treatmentId: payload.treatmentId,
      appointmentIds: payload.appointmentIds,
      onSuccess: payload.onSuccess,
    });
  };

  const openTreatmentBillModal = (payload: {
    matterId: string;
    billId?: string;
    treatmentId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    afterCreateFn?: (newBill: APIResponse<MedicalTreatmentBill>) => void;
    creatingFromAppointment?: boolean;
    initialData?: Partial<MedicalTreatmentBill>;
  }) => {
    return openSlideover({
      type: "treatment-bill",
      title: payload.editing
        ? SLIDEOVER_TITLE.TREATMENT_BILL.UPDATE
        : SLIDEOVER_TITLE.TREATMENT_BILL.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.TREATMENT_BILL.UPDATE
        : SLIDEOVER_DESCRIPTION.TREATMENT_BILL.CREATE,
      matterId: payload.matterId,
      billId: payload.billId,
      treatmentId: payload.treatmentId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      afterCreateFn: payload.afterCreateFn,
      creatingFromAppointment: payload.creatingFromAppointment,
      initialData: payload.initialData,
    });
  };

  const openTreatmentBalanceConfirmationModal = (payload: {
    matterId: string;
    treatmentId?: string;
    editing?: boolean;
    initialData?: Partial<MedicalTreatmentBalanceConfirmation>;
  }) => {
    return openSlideover({
      type: "treatment-balance-confirmation",
      title: payload.editing
        ? SLIDEOVER_TITLE.TREATMENT_BALANCE_CONFIRMATION.UPDATE
        : SLIDEOVER_TITLE.TREATMENT_BALANCE_CONFIRMATION.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.TREATMENT_BALANCE_CONFIRMATION.UPDATE
        : SLIDEOVER_DESCRIPTION.TREATMENT_BALANCE_CONFIRMATION.CREATE,
      matterId: payload.matterId,
      treatmentId: payload.treatmentId,
      editing: payload.editing,
      initialData: payload.initialData,
    });
  };

  const openTreatmentBalanceConfirmationHistoryModal = (payload: {
    matterId: string;
    treatmentId?: string;
  }) => {
    return openSlideover({
      type: "treatment-balance-confirmation-history",
      title: SLIDEOVER_TITLE.TREATMENT_BALANCE_CONFIRMATION_HISTORY.VIEW,
      description:
        SLIDEOVER_DESCRIPTION.TREATMENT_BALANCE_CONFIRMATION_HISTORY.VIEW,
      matterId: payload.matterId,
      treatmentId: payload.treatmentId,
    });
  };

  const openTreatmentRecordModal = (payload: {
    matterId: string;
    recordId?: string;
    treatmentId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    afterCreateFn?: (newRecord: APIResponse<MedicalTreatmentRecord>) => void;
    creatingFromAppointment?: boolean;
    initialData?: Partial<MedicalTreatmentRecord>;
  }) => {
    return openSlideover({
      type: "treatment-record",
      title: payload.editing
        ? SLIDEOVER_TITLE.TREATMENT_RECORD.UPDATE
        : SLIDEOVER_TITLE.TREATMENT_RECORD.CREATE,
      description: payload.editing
        ? SLIDEOVER_DESCRIPTION.TREATMENT_RECORD.UPDATE
        : SLIDEOVER_DESCRIPTION.TREATMENT_RECORD.CREATE,
      matterId: payload.matterId,
      recordId: payload.recordId,
      treatmentId: payload.treatmentId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      afterCreateFn: payload.afterCreateFn,
      creatingFromAppointment: payload.creatingFromAppointment,
      initialData: payload.initialData,
    });
  };

  const openTreatmentPlanModal = (payload: {
    appointmentId?: string;
    matterId: string;
    planId?: string;
    treatmentId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MedicalTreatmentPlan>;
  }) => {
    return openSlideover({
      type: "treatment-plan",
      title: payload.editing
        ? "Update treatment plan"
        : "Create treatment plan",
      description: payload.editing
        ? "Update a treatment plan"
        : "Create a treatment plan",
      appointmentId: payload.appointmentId,
      matterId: payload.matterId,
      planId: payload.planId,
      treatmentId: payload.treatmentId,
      editing: payload.editing,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
    });
  };

  const openMergeDocumentModal = (
    payload:
      | {
          matterId: string;
          massTortId?: never;
          initialSelectedFileIds?: string[];
        }
      | {
          massTortId: string;
          matterId?: never;
          initialSelectedFileIds?: string[];
        },
  ) => {
    return openSlideover({
      type: "merge-document",
      title: "Merge documents",
      description: "Merge documents",
      ...payload,
    });
  };

  const openIntakeResponseModal = (payload: { matterId: string }) => {
    return openSlideover({
      type: "intake-response",
      title: "Create intake response",
      description: "Select a template and respond to the questions",
      matterId: payload.matterId,
    });
  };

  const openSignatureRequestModal = (payload: {
    matterId: string;
    signatureRequestId: string;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    presentation?: "slideover" | "modal";
  }) => {
    return openSlideover({
      type: "signature-request",
      title: "Update signature request",
      description: "Update a signature request",
      matterId: payload.matterId,
      signatureRequestId: payload.signatureRequestId,
      editing: true,
      initialTab: payload.initialTab,
      presentation: payload.presentation,
    });
  };

  const openFirmSignatureRequestModal = (payload: {
    reportRow: ReportsSignatureRequestReport;
  }) => {
    return openSlideover({
      type: "firm-signature-request",
      title: "Update signature request",
      description: "Review this firm signature request.",
      reportRow: payload.reportRow,
    });
  };

  const openCreateSignatureRequestModal = (payload: {
    matterId?: string;
    subItem?: MatterDocumentSubItem;
  }) => {
    return openSlideover({
      type: "create-signature-request",
      title: "Create signature request",
      description: "Create a signature request",
      matterId: payload.matterId,
      subItem: payload.subItem,
    });
  };

  const getTrustCheckModalCopy = (
    editing?: boolean,
    trustCheckType?: MatterTrustCheck["type"],
  ) => {
    const defaultCopy = editing
      ? {
          title: "Update trust check",
          description: "Update the trust check below",
        }
      : {
          title: "Create trust check",
          description: "Create a new trust check",
        };

    if (trustCheckType === "Withdraw") {
      return {
        title: editing
          ? SLIDEOVER_TITLE.TRUST_CHECK_WITHDRAW.UPDATE
          : SLIDEOVER_TITLE.TRUST_CHECK_WITHDRAW.CREATE,
        description: editing
          ? SLIDEOVER_DESCRIPTION.TRUST_CHECK_WITHDRAW.UPDATE
          : SLIDEOVER_DESCRIPTION.TRUST_CHECK_WITHDRAW.CREATE,
      };
    }

    if (trustCheckType === "Deposit") {
      return {
        title: editing
          ? SLIDEOVER_TITLE.TRUST_CHECK_DEPOSIT.UPDATE
          : SLIDEOVER_TITLE.TRUST_CHECK_DEPOSIT.CREATE,
        description: editing
          ? SLIDEOVER_DESCRIPTION.TRUST_CHECK_DEPOSIT.UPDATE
          : SLIDEOVER_DESCRIPTION.TRUST_CHECK_DEPOSIT.CREATE,
      };
    }

    return defaultCopy;
  };

  const openTrustCheckModal = (payload: {
    matterId: string;
    trustCheckId?: string;
    trustCheckType?: MatterTrustCheck["type"];
    settlementOfferId?: string;
    settlementDemandId?: string;
    settlementCounterId?: string;
    expenseId?: string;
    lienId?: string;
    medicalTreatmentBillId?: string;
    editing?: boolean;
    initialTab?: "details" | "notes" | "tasks" | "references" | "events";
    initialData?: Partial<MatterTrustCheck>;
  }) => {
    const { title, description } = getTrustCheckModalCopy(
      payload.editing,
      payload.trustCheckType,
    );
    return openSlideover({
      type: "matter-trust-check",
      title,
      description,
      matterId: payload.matterId,
      trustCheckId: payload.trustCheckId,
      trustCheckType: payload.trustCheckType ?? undefined,
      editing: payload.editing,
      settlementOfferId: payload.settlementOfferId,
      settlementDemandId: payload.settlementDemandId,
      settlementCounterId: payload.settlementCounterId,
      expenseId: payload.expenseId,
      lienId: payload.lienId,
      medicalTreatmentBillId: payload.medicalTreatmentBillId,
      initialTab: payload.initialTab,
      initialData: payload.initialData,
    });
  };

  const openCaseIncidentDescriptionModal = (payload: { matterId: string }) => {
    return openSlideover({
      type: "case-incident-description",
      title: "-",
      description: "-",
      matterId: payload.matterId,
    });
  };

  const openCaseInjuriesModal = (payload: { matterId: string }) => {
    return openSlideover({
      type: "case-injuries",
      title: "-",
      description: "-",
      matterId: payload.matterId,
    });
  };

  const openMatterCriticalInfoModal = (payload: { matterId: string }) => {
    return openSlideover({
      type: "matter-critical-info",
      title: "-",
      description: "-",
      matterId: payload.matterId,
    });
  };

  const openFileModal = (payload: {
    matterId?: string;
    massTortId?: string;
    activeFileId?: string;
    editing?: boolean;
    currentFolderId: string;
    isFolder: boolean;
    handleDeleteItem?: () => Promise<void>;
    isCriticalImage?: boolean;
    autoOpenUpload?: boolean;
    initialFile?: File;
    afterCreateFile?: (file: MatterFile) => Promise<void> | void;
    showPreview?: boolean;
  }) => {
    return openSlideover({
      type: "file",
      title: payload.editing
        ? payload.isFolder
          ? SLIDEOVER_TITLE.FOLDER.UPDATE
          : SLIDEOVER_TITLE.FILE.UPDATE
        : payload.isFolder
          ? SLIDEOVER_TITLE.FOLDER.CREATE
          : SLIDEOVER_TITLE.FILE.CREATE,
      description: payload.editing
        ? payload.isFolder
          ? SLIDEOVER_DESCRIPTION.FOLDER.UPDATE
          : SLIDEOVER_DESCRIPTION.FILE.UPDATE
        : payload.isFolder
          ? SLIDEOVER_DESCRIPTION.FOLDER.CREATE
          : SLIDEOVER_DESCRIPTION.FILE.CREATE,
      matterId: payload.matterId,
      massTortId: payload.massTortId,
      activeFileId: payload.activeFileId,
      editing: payload.editing,
      currentFolderId: payload.currentFolderId,
      isFolder: payload.isFolder,
      handleDeleteItem: payload.handleDeleteItem,
      isCriticalImage: payload.isCriticalImage,
      autoOpenUpload: payload.autoOpenUpload,
      initialFile: payload.initialFile,
      afterCreateFile: payload.afterCreateFile,
      showPreview: payload.showPreview,
    });
  };

  const openShareFolderModal = (payload: {
    matterId: string;
    initialSelectedFileIds?: string[];
    initialSelectedContactIds?: string[];
    shareFolderId?: string;
    editing?: boolean;
    title?: string;
    description?: string;
  }) => {
    const isEditing = payload.editing ?? false;
    return openSlideover({
      type: "share-folder",
      title:
        payload.title ??
        (isEditing
          ? SLIDEOVER_TITLE.SHARE_FOLDER.UPDATE
          : SLIDEOVER_TITLE.SHARE_FOLDER.CREATE),
      description:
        payload.description ??
        (isEditing
          ? SLIDEOVER_DESCRIPTION.SHARE_FOLDER.UPDATE
          : SLIDEOVER_DESCRIPTION.SHARE_FOLDER.CREATE),
      matterId: payload.matterId,
      initialSelectedFileIds: payload.initialSelectedFileIds,
      initialSelectedContactIds: payload.initialSelectedContactIds,
      shareFolderId: payload.shareFolderId,
      editing: isEditing,
    });
  };

  const openShareFolderFileModal = (payload: {
    matterId: string;
    matterShareFolderFileId: string;
    matterShareFolderFilePresignedUrlKey: string;
    matterFileName: string;
    matterShareFolderId?: string;
    shareFolderLabel?: string;
  }) => {
    const baseDescription = payload.shareFolderLabel
      ? `Shared from ${payload.shareFolderLabel}`
      : "Preview shared file";

    return openSlideover({
      type: "share-folder-file",
      title: payload.matterFileName,
      description: baseDescription,
      matterId: payload.matterId,
      matterShareFolderId: payload.matterShareFolderId,
      matterShareFolderFileId: payload.matterShareFolderFileId,
      matterShareFolderFilePresignedUrlKey:
        payload.matterShareFolderFilePresignedUrlKey,
      matterFileName: payload.matterFileName,
      shareFolderLabel: payload.shareFolderLabel,
    });
  };

  const openShareFolderAccessModal = (payload: {
    matterId: string;
    shareFolderId: string;
    title?: string;
    description?: string;
  }) => {
    return openSlideover({
      type: "share-folder-access",
      title: payload.title ?? "Share folder access",
      description:
        payload.description ?? "Send shared folder access information.",
      matterId: payload.matterId,
      shareFolderId: payload.shareFolderId,
    });
  };

  const openAddRelatedMatterModal = (payload: { matterId: string }) => {
    return openSlideover({
      type: "add-related-matter",
      title: "Add related matter",
      description: "Select a matter to relate to this matter",
      matterId: payload.matterId,
    });
  };

  const openGenerateDocumentModal = (payload: {
    matter: Matter;
    contacts: Contact[];
    matterFileIdLocation?: string;
    afterGenerateFn?: (results: MatterFile[]) => void;
    templateType?: (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number];
    restrictTemplatesToType?: boolean;
    hideSaveAndMerge?: boolean;
    subItem?: MatterDocumentSubItem;
  }) => {
    return openSlideover({
      type: "generate-document",
      title: "Generate document",
      description: "Generate a document",
      matter: payload.matter,
      contacts: payload.contacts,
      matterFileIdLocation: payload.matterFileIdLocation,
      afterGenerateFn: payload.afterGenerateFn,
      templateType: payload.templateType,
      restrictTemplatesToType: payload.restrictTemplatesToType,
      hideSaveAndMerge: payload.hideSaveAndMerge,
      subItem: payload.subItem,
    });
  };

  const openMatterContactConnectionModal = (payload: {
    matterId: string;
    previewContact?: Contact;
    matterContactConnectionId?: string;
    matterContactConnectionContactId?: string;
    matterContactConnection?: MatterContactConnection;
    disableDelete?: boolean;
    labelOptions?: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
    disableTime?: boolean;
    editing?: boolean;
    title?: string;
    description?: string;
    autoOpenContactSelection?: boolean;
    selectContactOnly?: boolean;
    defaultCreateContactValues?: Partial<
      z.infer<typeof createContactFormSchema>
    >;
    defaultContactSearchObject?: { [key: string]: string[] };
    excludeContactIds?: string[];
    reservedContactLabelPairs?: MatterContactLabelPair[];
    slideoverId?: string;
    editPresentation?: "slideover" | "modal";
    createPresentation?: "slideover" | "modal";
  }) => {
    const defaultEditTitle =
      payload.matterContactConnection != null
        ? `Edit ${payload.matterContactConnection.label} information`
        : SLIDEOVER_TITLE.MATTER_CONTACT_CONNECTION.UPDATE;

    return openSlideover({
      type: "matter-contact-connection",
      slideoverId: payload.slideoverId,
      title:
        payload.title ??
        (payload.editing
          ? defaultEditTitle
          : SLIDEOVER_TITLE.MATTER_CONTACT_CONNECTION.CREATE),
      description:
        payload.description ??
        (payload.editing
          ? SLIDEOVER_DESCRIPTION.MATTER_CONTACT_CONNECTION.UPDATE
          : SLIDEOVER_DESCRIPTION.MATTER_CONTACT_CONNECTION.CREATE),
      matterId: payload.matterId,
      previewContact: payload.previewContact,
      matterContactConnectionId: payload.matterContactConnectionId,
      matterContactConnectionContactId:
        payload.matterContactConnectionContactId,
      matterContactConnection: payload.matterContactConnection,
      disableDelete: payload.disableDelete,
      disableTime: payload.disableTime,
      autoOpenContactSelection: payload.autoOpenContactSelection,
      selectContactOnly: payload.selectContactOnly,
      labelOptions: payload.labelOptions,
      defaultCreateContactValues: payload.defaultCreateContactValues,
      defaultContactSearchObject: payload.defaultContactSearchObject,
      excludeContactIds: payload.excludeContactIds,
      reservedContactLabelPairs: payload.reservedContactLabelPairs,
      editPresentation: payload.editPresentation,
      createPresentation: payload.createPresentation,
    });
  };

  return {
    openAddRelatedMatterModal,
    openCaseIncidentDescriptionModal,
    openCaseInjuriesModal,
    openCommunicationModal,
    openPhoneCallLogModal,
    openExpenseModal,
    openFileModal,
    openGenerateDocumentModal,
    openIncidentReportModal,
    openInsuranceModal,
    openIntakeResponseModal,
    openLienModal,
    openLitigationDateModal,
    openLitigationDatePlanConnectionModal,
    openLostWageModal,
    openMatterContactConnectionModal,
    openMatterCriticalInfoModal,
    openMatterEmailViewModal,
    openMedicalTreatmentModal,
    openMergeDocumentModal,
    openNoteModal,
    openPropertyDamageModal,
    openSendEmailModal,
    openSendMatterEmailModal,
    openSettlementCounterModal,
    openSettlementDemandModal,
    openShareFolderAccessModal,
    openShareFolderFileModal,
    openShareFolderModal,
    openCreateSignatureRequestModal,
    openFirmSignatureRequestModal,
    openSignatureRequestModal,
    openTaskModal,
    openTreatmentAppointmentModal,
    openAssociateTreatmentAppointmentsModal,
    openTreatmentBillModal,
    openTreatmentBalanceConfirmationModal,
    openTreatmentBalanceConfirmationHistoryModal,
    openTreatmentPlanModal,
    openTreatmentRecordModal,
    openTrustCheckModal,
    openVehicleModal,
    closeSlideover,
    closeAllSlideovers,
    getSlideover,
  };
};

export const useHandleSearchParams = () => {
  const navigate = useNavigate();
  const {
    openLostWageModal,
    openTaskModal,
    openLienModal,
    openPropertyDamageModal,
    openInsuranceModal,
    openVehicleModal,
    openNoteModal,
    openSettlementDemandModal,
    openSettlementCounterModal,
    openExpenseModal,
    openMedicalTreatmentModal,
    openTreatmentAppointmentModal,
    openTreatmentBillModal,
    openTreatmentRecordModal,
    openTreatmentPlanModal,
    openTrustCheckModal,
    openLitigationDateModal,
  } = useGlobalModal();

  const handleVehicleSearch = useCallback(
    (matter: Matter, searchVehicleId?: string) => {
      if (!searchVehicleId) return;
      const selectedVehicle = matter.vehicles.find(
        (vehicle) => vehicle.id === searchVehicleId,
      );
      if (selectedVehicle) {
        openVehicleModal({
          matterId: matter.id,
          vehicleId: selectedVehicle.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("vehicleId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openVehicleModal],
  );

  const handleInsuranceSearch = useCallback(
    (matter: Matter, searchInsuranceId?: string) => {
      if (!searchInsuranceId) return;
      const selectedInsurance = matter.insurances.find(
        (insurance) => insurance.id === searchInsuranceId,
      );
      if (selectedInsurance) {
        openInsuranceModal({
          matterId: matter.id,
          insuranceId: selectedInsurance.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("insuranceId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openInsuranceModal],
  );

  const handlePropertyDamageSearch = useCallback(
    (matter: Matter, searchPropertyDamageId?: string) => {
      if (!searchPropertyDamageId) return;
      const selectedPropertyDamage = matter.propertyDamages.find(
        (propertyDamage) => propertyDamage.id === searchPropertyDamageId,
      );
      if (selectedPropertyDamage) {
        openPropertyDamageModal({
          matterId: matter.id,
          propertyDamageId: selectedPropertyDamage.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("propertyDamageId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openPropertyDamageModal],
  );

  const handleTaskSearch = useCallback(
    (matter: Matter, searchTaskId?: string) => {
      if (!searchTaskId) return;
      openTaskModal({
        matterId: matter.id,
        taskId: searchTaskId,
        editing: true,
        initialTab: "details",
      });
      const url = new URL(window.location.href);
      url.searchParams.delete("taskId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openTaskModal],
  );

  const handleLienSearch = useCallback(
    (matter: Matter, searchLienId?: string) => {
      if (!searchLienId) return;
      const selectedLien = matter.liens.find(
        (lien) => lien.id === searchLienId,
      );
      if (selectedLien) {
        openLienModal({
          matterId: matter.id,
          lienId: selectedLien.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("lienId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openLienModal],
  );

  const handleLostWageSearch = useCallback(
    (matter: Matter, searchLostWageId?: string) => {
      if (!searchLostWageId) return;
      const selectedLostWage = matter.lostWages.find(
        (lostWage) => lostWage.id === searchLostWageId,
      );
      if (selectedLostWage) {
        openLostWageModal({
          matterId: matter.id,
          lostWageId: selectedLostWage.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("lostWageId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openLostWageModal],
  );

  const handleNoteSearch = useCallback(
    (matter: Matter, searchNoteId?: string) => {
      if (!searchNoteId) return;
      openNoteModal({
        matterId: matter.id,
        noteId: searchNoteId,
        editing: true,
        initialTab: "details",
      });
      const url = new URL(window.location.href);
      url.searchParams.delete("noteId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openNoteModal],
  );

  const handleSettlementDemandSearch = useCallback(
    (matter: Matter, searchSettlementId?: string) => {
      if (!searchSettlementId) return;
      const selectedSettlementDemand = matter.settlementDemands.find(
        (s) => s.id === searchSettlementId,
      );
      const selectedSettlementOffer = matter.settlementOffers.find(
        (s) => s.id === searchSettlementId,
      );
      const selectedSettlementCounter = matter.settlementCounters.find(
        (s) => s.id === searchSettlementId,
      );
      const selectedSettlement =
        selectedSettlementDemand ||
        selectedSettlementOffer ||
        selectedSettlementCounter;

      if (selectedSettlement) {
        if (selectedSettlementCounter) {
          openSettlementCounterModal({
            matterId: matter.id,
            settlementCounterId: selectedSettlementCounter.id,
            editing: true,
            initialTab: "details",
          });
        } else {
          openSettlementDemandModal({
            matterId: matter.id,
            settlementId:
              selectedSettlementDemand?.id ?? selectedSettlementOffer?.id,
            editing: true,
            initialTab: "details",
            negotiationType: selectedSettlementDemand
              ? "Demand"
              : selectedSettlementOffer
                ? "Offer"
                : undefined,
          });
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("settlementDemandId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openSettlementCounterModal, openSettlementDemandModal],
  );

  const handleExpenseSearch = useCallback(
    (matter: Matter, searchExpenseId?: string) => {
      if (!searchExpenseId) return;
      const selectedExpense = matter.expenses.find(
        (expense) => expense.id === searchExpenseId,
      );
      if (selectedExpense) {
        openExpenseModal({
          matterId: matter.id,
          expenseId: selectedExpense.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("expenseId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openExpenseModal],
  );

  const handleMedicalTreatmentSearch = useCallback(
    (matter: Matter, searchTreatmentId?: string) => {
      if (!searchTreatmentId) return;
      const selectedTreatment = matter.medicalTreatments.find(
        (t) => t.id === searchTreatmentId,
      );
      if (selectedTreatment) {
        openMedicalTreatmentModal({
          matterId: matter.id,
          treatmentId: selectedTreatment.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("treatmentId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openMedicalTreatmentModal],
  );

  const handleTreatmentAppointmentSearch = useCallback(
    (matter: Matter, searchAppointmentId?: string) => {
      if (!searchAppointmentId) return;
      const selectedAppointment = matter.medicalTreatmentAppointments.find(
        (a) => a.id === searchAppointmentId,
      );
      if (selectedAppointment) {
        openTreatmentAppointmentModal({
          matterId: matter.id,
          appointmentId: selectedAppointment.id,
          editing: true,
          initialTab: "details",
          treatmentId: selectedAppointment.medicalTreatmentId,
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("appointmentId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openTreatmentAppointmentModal],
  );

  const handleTreatmentBillSearch = useCallback(
    (matter: Matter, searchBillId?: string) => {
      if (!searchBillId) return;
      const selectedBill = matter.medicalTreatmentBills.find(
        (bill) => bill.id === searchBillId,
      );
      if (selectedBill) {
        openTreatmentBillModal({
          matterId: matter.id,
          billId: selectedBill.id,
          editing: true,
          initialTab: "details",
          treatmentId: selectedBill.medicalTreatmentId,
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("billId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openTreatmentBillModal],
  );

  const handleTreatmentRecordSearch = useCallback(
    (matter: Matter, searchRecordId?: string) => {
      if (!searchRecordId) return;
      const selectedRecord = matter.medicalTreatmentRecords.find(
        (r) => r.id === searchRecordId,
      );
      if (selectedRecord) {
        openTreatmentRecordModal({
          matterId: matter.id,
          recordId: selectedRecord.id,
          editing: true,
          initialTab: "details",
          treatmentId: selectedRecord.medicalTreatmentId,
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("recordId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openTreatmentRecordModal],
  );

  const handleTreatmentPlanSearch = useCallback(
    (matter: Matter, searchPlanId?: string) => {
      if (!searchPlanId) return;
      const selectedPlan = matter.medicalTreatmentPlans.find(
        (plan) => plan.id === searchPlanId,
      );
      if (selectedPlan) {
        const selectedPlanAppointmentConnection =
          matter.medicalTreatmentAppointmentConnections.find(
            (connection) =>
              connection.medicalTreatmentPlanId === selectedPlan.id,
          );
        openTreatmentPlanModal({
          matterId: matter.id,
          appointmentId:
            selectedPlanAppointmentConnection?.medicalTreatmentAppointmentId,
          planId: selectedPlan.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("planId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openTreatmentPlanModal],
  );

  const handleTrustCheckSearch = useCallback(
    (matter: Matter, searchTrustCheckId?: string) => {
      if (!searchTrustCheckId) return;
      const selectedTrustCheck = matter.trustChecks.find(
        (tc) => tc.id === searchTrustCheckId,
      );
      if (selectedTrustCheck) {
        openTrustCheckModal({
          matterId: matter.id,
          trustCheckId: selectedTrustCheck.id,
          trustCheckType: selectedTrustCheck.type,
          editing: true,
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("trustCheckId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openTrustCheckModal],
  );

  const handleLitigationDateSearch = useCallback(
    (matter: Matter, searchLitigationDateId?: string) => {
      if (!searchLitigationDateId) return;
      const selectedLitigationDate = matter.litigationDates.find(
        (litigationDate) => litigationDate.id === searchLitigationDateId,
      );
      if (selectedLitigationDate) {
        openLitigationDateModal({
          matterId: matter.id,
          litigationDateId: selectedLitigationDate.id,
          editing: true,
          initialTab: "details",
        });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("litigationDateId");
      void navigate({ to: url.pathname + url.search, replace: true });
    },
    [navigate, openLitigationDateModal],
  );

  return {
    handleVehicleSearch,
    handleInsuranceSearch,
    handlePropertyDamageSearch,
    handleTaskSearch,
    handleLienSearch,
    handleLostWageSearch,
    handleNoteSearch,
    handleSettlementDemandSearch,
    handleExpenseSearch,
    handleMedicalTreatmentSearch,
    handleTreatmentAppointmentSearch,
    handleTreatmentBillSearch,
    handleTreatmentRecordSearch,
    handleTreatmentPlanSearch,
    handleTrustCheckSearch,
    handleLitigationDateSearch,
  };
};
